import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Users, FolderOpen } from "lucide-react";
import { useGroups, useCreateGroup, useDeleteGroup } from "../../application/hooks/useGroups";
import { useConfirm } from "@tabipla/components";
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@tabipla/ui";
import { DashboardLayout } from "../layouts/DashboardLayout";
import { LoadingState } from "../components/LoadingState";
import { ErrorState } from "../components/ErrorState";
import { EmptyState } from "../components/EmptyState";
import { GroupCard } from "../components/groups/GroupCard";

export function Groups() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: groups, isLoading, error, refetch } = useGroups();
  const createMutation = useCreateGroup();
  const deleteMutation = useDeleteGroup();
  const { confirm, ConfirmDialog } = useConfirm();

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDescription, setNewGroupDescription] = useState("");

  const handleCreateGroup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGroupName.trim()) return;

    try {
      const group = await createMutation.mutateAsync({
        name: newGroupName,
        description: newGroupDescription || null,
      });
      toast.success(t("groups.createSuccess", "グループを作成しました"));
      setShowCreateDialog(false);
      setNewGroupName("");
      setNewGroupDescription("");
      if (group) {
        navigate(`/groups/${group.id}`);
      }
    } catch {
      toast.error(t("groups.createError", "作成に失敗しました"));
    }
  };

  const handleDeleteGroup = async (groupId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const confirmed = await confirm({
      title: t("groups.deleteTitle", "グループを削除"),
      description: t("groups.deleteConfirm"),
      confirmText: t("common.delete", "削除"),
      variant: "destructive",
    });

    if (confirmed) {
      try {
        await deleteMutation.mutateAsync(groupId);
        toast.success(t("groups.deleteSuccess", "グループを削除しました"));
      } catch {
        toast.error(t("groups.deleteError", "削除に失敗しました"));
      }
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title={t("groups.title")}>
        <LoadingState message={t("groups.loading")} />
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout title={t("groups.title")}>
        <ErrorState error={error} onRetry={refetch} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={t("groups.title")}
      fabAction={{
        icon: Plus,
        label: t("groups.createNew"),
        onClick: () => setShowCreateDialog(true),
      }}
    >
      <div className="animate-fade-in space-y-6">
        {/* Header - Desktop only */}
        <div className="hidden lg:block">
          <h1 className="text-2xl font-bold tracking-tight">{t("groups.title")}</h1>
          <p className="text-muted-foreground">{t("groups.subtitle")}</p>
        </div>

        {/* Groups List */}
        {!groups || groups.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={t("groups.noGroups")}
            description={t(
              "groups.noGroupsDescription",
              "グループを作成して仲間と旅行を計画しましょう"
            )}
            action={{
              label: t("groups.createNew"),
              onClick: () => setShowCreateDialog(true),
              icon: Plus,
            }}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((group) => (
              <GroupCard
                key={group.id}
                group={group}
                onClick={() => navigate(`/groups/${group.id}`)}
                onDelete={(e) => handleDeleteGroup(group.id, e)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create Group Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              {t("groups.createTitle")}
            </DialogTitle>
            <DialogDescription>
              {t("groups.createDescription", "新しいグループを作成して仲間と旅行を計画しましょう")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateGroup}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">{t("groups.name")}</label>
                <Input
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t("groups.namePlaceholder")}
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">
                  {t("groups.description")}
                  <span className="ml-1 text-xs text-muted-foreground">
                    ({t("common.optional")})
                  </span>
                </label>
                <Input
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder={t("groups.descriptionPlaceholder")}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="flex-1 sm:flex-none"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={createMutation.isPending || !newGroupName.trim()}
                className="flex-1 sm:flex-none"
              >
                {createMutation.isPending ? t("groups.creating") : t("groups.create")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </DashboardLayout>
  );
}
